import { useState, useEffect } from "react";
import API from "@/lib/api";
import { formatCurrency, formatPercent, formatMultiple } from "@/lib/formatters";
import MetricCard from "@/components/shared/MetricCard";
import DataTable from "@/components/shared/DataTable";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Landmark, TrendingUp, DollarSign, BarChart3, Plus, Upload, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import AddEditDialog from "@/components/shared/AddEditDialog";
import UploadDialog from "@/components/shared/UploadDialog";
import DeleteConfirmDialog from "@/components/shared/DeleteConfirmDialog";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as ReTooltip, Legend,
} from "recharts";

const STRATEGIES = ["All", "Opportunistic", "Value-Add", "Core", "Core-Plus", "Debt"];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#151B24] border border-[#2D3748] rounded-sm p-3 text-sm shadow-xl">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="font-data text-xs" style={{ color: p.fill }}>{p.dataKey}: {p.value}</p>
      ))}
    </div>
  );
};

export default function Funds() {
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [strategyFilter, setStrategyFilter] = useState("All");
  const [showAdd, setShowAdd] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    const params = {};
    if (strategyFilter !== "All") params.strategy = strategyFilter;
    API.get("/funds", { params })
      .then((res) => setFunds(res.data.funds || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [strategyFilter, refreshKey]);

  const avgIrr = funds.length ? funds.reduce((a, f) => a + f.irr, 0) / funds.length : 0;
  const avgMoic = funds.length ? funds.reduce((a, f) => a + f.moic, 0) / funds.length : 0;
  const totalNav = funds.reduce((a, f) => a + f.nav, 0);
  const totalDist = funds.reduce((a, f) => a + f.distributions, 0);

  const columns = [
    { key: "name", label: "Fund", sortable: false, render: (v) => <span className="font-medium text-foreground">{v}</span> },
    { key: "manager", label: "Manager" },
    { key: "strategy", label: "Strategy", render: (v) => <Badge variant="secondary" className="font-data text-xs">{v}</Badge> },
    { key: "vintage_year", label: "Vintage", align: "right" },
    { key: "nav", label: "NAV", align: "right", render: (v) => formatCurrency(v) },
    { key: "irr", label: "IRR", align: "right", render: (v) => <span className="text-emerald-500">{v}%</span> },
    { key: "moic", label: "MOIC", align: "right", render: (v) => formatMultiple(v) },
    { key: "dpi", label: "DPI", align: "right", render: (v) => formatMultiple(v) },
    { key: "tvpi", label: "TVPI", align: "right", render: (v) => formatMultiple(v) },
    { key: "status", label: "Status", render: (v) => (
      <Badge variant={v === "Active" ? "default" : "secondary"} className="font-data text-xs">{v}</Badge>
    )},
    { key: "actions", label: "", sortable: false, render: (_, row) => (
      <div className="flex gap-1">
        <button className="p-1 rounded-sm hover:bg-secondary transition-colors" onClick={(e) => { e.stopPropagation(); setEditItem(row); }} data-testid={`edit-${row.id}`}><Pencil size={14} className="text-muted-foreground hover:text-foreground" /></button>
        <button className="p-1 rounded-sm hover:bg-destructive/20 transition-colors" onClick={(e) => { e.stopPropagation(); setDeleteItem(row); }} data-testid={`delete-${row.id}`}><Trash2 size={14} className="text-muted-foreground hover:text-red-500" /></button>
      </div>
    )},
  ];

  if (loading) return <div className="p-6"><Skeleton className="h-96" /></div>;

  return (
    <div className="p-6 space-y-6" data-testid="funds-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Fund Performance</h1>
          <p className="text-sm text-muted-foreground mt-1">IRR, MOIC, DPI, TVPI across funds</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={strategyFilter} onValueChange={setStrategyFilter}>
            <SelectTrigger className="w-48 bg-card border-border" data-testid="strategy-filter">
              <SelectValue placeholder="Filter by Strategy" />
            </SelectTrigger>
            <SelectContent>
              {STRATEGIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-fund-btn" className="gap-1"><Plus size={14} />Add</Button>
          <Button size="sm" variant="secondary" onClick={() => setShowUpload(true)} data-testid="upload-fund-btn" className="gap-1"><Upload size={14} />Upload</Button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <MetricCard label="Total NAV" value={formatCurrency(totalNav)} icon={Landmark} />
        <MetricCard label="Average IRR" value={formatPercent(avgIrr)} icon={TrendingUp} />
        <MetricCard label="Average MOIC" value={formatMultiple(avgMoic)} icon={BarChart3} />
        <MetricCard label="Total Distributions" value={formatCurrency(totalDist)} icon={DollarSign} />
      </div>

      <Card className="border border-border bg-card">
        <CardHeader className="border-b border-border p-3">
          <CardTitle className="text-sm font-semibold">IRR by Fund</CardTitle>
        </CardHeader>
        <CardContent className="p-4" data-testid="fund-irr-chart">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={funds.slice(0, 10)} margin={{ left: 10, bottom: 40 }}>
              <CartesianGrid strokeDasharray="4 4" stroke="#2D3748" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: "#9CA3AF", fontSize: 10 }} angle={-35} textAnchor="end" axisLine={false} tickLine={false} interval={0} />
              <YAxis tick={{ fill: "#9CA3AF", fontSize: 12 }} axisLine={false} tickLine={false} />
              <ReTooltip content={<CustomTooltip />} />
              <Bar dataKey="irr" fill="#3B82F6" radius={[2, 2, 0, 0]} barSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card className="border border-border bg-card">
        <CardHeader className="border-b border-border p-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">All Funds</CardTitle>
            <span className="text-xs text-muted-foreground font-data">{funds.length} funds</span>
          </div>
        </CardHeader>
        <CardContent className="p-0" data-testid="funds-table">
          <DataTable columns={columns} data={funds} />
        </CardContent>
      </Card>
      <AddEditDialog open={showAdd || !!editItem} item={editItem} entityType="funds" onClose={() => { setShowAdd(false); setEditItem(null); }} onSave={refresh} />
      <UploadDialog open={showUpload} entityType="funds" onClose={() => setShowUpload(false)} onSuccess={refresh} />
      <DeleteConfirmDialog open={!!deleteItem} item={deleteItem} entityType="funds" onClose={() => setDeleteItem(null)} onDelete={refresh} />
    </div>
  );
}
